import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

export default function ProductDetail() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [mainImage, setMainImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [loading, setLoading] = useState(true);
  const [wishlisted, setWishlisted] = useState(false);
  const [wishId, setWishId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`catalog/products/${slug}/`)
      .then(res => {
        setProduct(res.data);
        const v = res.data.variants;
        if (v?.length) { setSelectedSize(v[0].size || ''); setSelectedColor(v[0].color || ''); }
      })
      .catch(() => toast.error('Product not found'))
      .finally(() => setLoading(false));
    api.get('reviews/', { params: { product: slug } })
      .then(res => setReviews(res.data.results || res.data))
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!user || !product) return;
    api.get('auth/wishlist/').then(r => {
      const found = (r.data.results || r.data).find(w => w.product === product.id);
      if (found) { setWishlisted(true); setWishId(found.id); }
    }).catch(() => {});
  }, [user, product]);

  const handleWishlist = async () => {
    if (!user) { toast.error('Login to add to wishlist'); return; }
    try {
      if (wishlisted && wishId) {
        await api.delete(`auth/wishlist/${wishId}/`);
        setWishlisted(false); setWishId(null);
        toast.success('Removed from wishlist');
      } else {
        const { data } = await api.post('auth/wishlist/', { product: product.id });
        setWishlisted(true); setWishId(data.id);
        toast.success('Added to wishlist');
      }
      window.dispatchEvent(new Event('wishlist-updated'));
    } catch { toast.error('Failed'); }
  };

  if (loading) return <p style={{ textAlign: 'center', padding: 60, color: '#999' }}>Loading...</p>;
  if (!product) return <p style={{ textAlign: 'center', padding: 60 }}>Product not found.</p>;

  const images = product.images?.length ? product.images : [{ image: product.image }];
  const sizes = [...new Set(product.variants?.map(v => v.size).filter(Boolean))];
  const colors = [...new Set(product.variants?.map(v => v.color).filter(Boolean))];
  const avgRating = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  const handleAddToCart = async () => {
    if (!user) { toast.error('Please login to add to cart'); return; }
    if (sizes.length && !selectedSize) { toast.error('Select a size'); return; }
    if (colors.length && !selectedColor) { toast.error('Select a color'); return; }
    const variant = product.variants?.find(v => (!sizes.length || v.size === selectedSize) && (!colors.length || v.color === selectedColor));
    if (!variant) { toast.error('Variant not available'); return; }
    try {
      await addToCart(variant.id, 1);
      toast.success('Added to cart');
    } catch {
      toast.error('Failed to add to cart');
    }
  };

  const label = { fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: '#666', marginBottom: 6 };

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 20px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48 }}>
        {/* Images */}
        <div>
          <img src={images[mainImage]?.image} alt={product.name} style={{ width: '100%', aspectRatio: '3/4', objectFit: 'cover', marginBottom: 12 }} />
          {images.length > 1 && (
            <div style={{ display: 'flex', gap: 8 }}>
              {images.map((img, i) => (
                <img key={i} src={img.image} alt="" onClick={() => setMainImage(i)}
                  style={{ width: 64, height: 80, objectFit: 'cover', cursor: 'pointer', border: i === mainImage ? '2px solid #1a1a1a' : '2px solid transparent' }} />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 300, letterSpacing: 1, marginBottom: 8 }}>{product.name}</h1>
          <p style={{ fontSize: 22, marginBottom: 24 }}>
            {product.sale_price && product.sale_price !== product.base_price ? (
              <>
                <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 12 }}>₹{product.base_price}</span>
                <span style={{ color: '#c00' }}>₹{product.sale_price}</span>
              </>
            ) : (
              <span>₹{product.base_price}</span>
            )}
          </p>
          <p style={{ color: '#555', lineHeight: 1.7, marginBottom: 24 }}>{product.description}</p>

          {product.fabric && <p style={{ marginBottom: 8 }}><span style={label}>Fabric: </span>{product.fabric}</p>}
          {product.care_instructions && <p style={{ marginBottom: 24 }}><span style={label}>Care: </span>{product.care_instructions}</p>}

          {sizes.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <p style={label}>Size</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {sizes.map(s => (
                  <button key={s} onClick={() => setSelectedSize(s)}
                    style={{ padding: '8px 16px', border: '1px solid', borderColor: selectedSize === s ? '#1a1a1a' : '#ddd', background: selectedSize === s ? '#1a1a1a' : '#fff', color: selectedSize === s ? '#fff' : '#333', cursor: 'pointer' }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {colors.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <p style={label}>Color</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {colors.map(c => (
                  <button key={c} onClick={() => setSelectedColor(c)}
                    style={{ padding: '8px 16px', border: '1px solid', borderColor: selectedColor === c ? '#1a1a1a' : '#ddd', background: selectedColor === c ? '#1a1a1a' : '#fff', color: selectedColor === c ? '#fff' : '#333', cursor: 'pointer' }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(() => {
            const v = product.variants?.find(v => (!sizes.length || v.size === selectedSize) && (!colors.length || v.color === selectedColor));
            if (v && v.stock > 0 && v.stock <= 5) return <p style={{ color: '#e53935', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>🔥 Only {v.stock} left in stock!</p>;
            if (v && v.stock === 0) return <p style={{ color: '#999', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Out of stock</p>;
            return null;
          })()}

          <div style={{ display: 'flex', gap: 12 }}>
            <button onClick={handleAddToCart}
              style={{ flex: 1, padding: '14px', background: '#1a1a1a', color: '#fff', border: 'none', fontSize: 14, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer' }}>
              Add to Cart
            </button>
            <button onClick={handleWishlist}
              style={{ width: 52, border: '1px solid #ddd', background: wishlisted ? '#C9A96E' : '#fff', color: wishlisted ? '#fff' : '#333', cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ♥
            </button>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section style={{ marginTop: 60 }}>
        <h2 style={{ fontSize: 20, fontWeight: 300, letterSpacing: 1, marginBottom: 20 }}>
          Reviews {avgRating && <span style={{ color: '#999' }}>— {avgRating} ★</span>}
        </h2>
        {reviews.length === 0 ? (
          <p style={{ color: '#999' }}>No reviews yet.</p>
        ) : (
          reviews.map(r => (
            <div key={r.id} style={{ borderBottom: '1px solid #eee', padding: '16px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <strong style={{ fontWeight: 500 }}>{r.user?.username || 'Anonymous'}</strong>
                <span style={{ color: '#999' }}>{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
              </div>
              <p style={{ color: '#555' }}>{r.comment}</p>
            </div>
          ))
        )}
      </section>
    </div>
  );
}
